import {
  BinderDto,
  MassConcentrationDto,
  PumpType,
  RheologyDataDto,
  ThroughputControlType,
  UcsCoefficientsDto
} from 'providers/api';
import { isNumber } from 'utils/number';
import { calculateYieldfromConcentrationByMass } from 'utils/backfill';
import { firstSelectableRheology } from 'utils/rheology';
import { calculateFriction, calculateFrictionForRoute } from '../Model/updateFriction';
import { updateRecipeState } from '../Model/updateRecipeState';
import { binderFromWaterBinderRatioAndBinder, roundUpBinderConc, waterBinderRatioFromConcentrationAndBinder } from './PCOtherFunctions';
import { calculateUCSfromWCandDays, calculateWCfromUCSandDays } from './ucsFunctions';
import {
  OptimisationErrorType,
  OptimisationTargetType,
  NominalOptimisationParams,
  OptimisationTargetLineInput,
  OptimisationTargetLineResult,
  PasteOptimiseResult,
  RecipeState,
  PasteRecipe
} from '../../MineModel/types';
import PCRoute from '../../MineModel/Route/PCRoute';
import { Model } from '../../hooks/useModelState';
import { returnShortestRoute } from '../../MineModel/Route/utils';

export function getPointsWithBoosterPressure(selectedRoute: PCRoute | undefined) {
  if (!selectedRoute || !Array.isArray(selectedRoute.routePoints)) {
    return [];
  }

  return selectedRoute.routePoints
    .filter((routePoint) => routePoint.point && routePoint.point.boosterPressure > 0)
    .map((routePoint) => routePoint);
}

const calculateActualUCS = (
  currentMassConcentration: number,
  binderConcentrationByMass: number,
  ucsCoefficients?: UcsCoefficientsDto,
): number => {
  if (!ucsCoefficients) return 0;
  const finalWC = waterBinderRatioFromConcentrationAndBinder(currentMassConcentration, binderConcentrationByMass);
  return calculateUCSfromWCandDays(finalWC, ucsCoefficients.coefficient1, ucsCoefficients.coefficient2);
};

export const getUCSTargetDays = (ucsCoefficients: Record<number, UcsCoefficientsDto>): number => +(Object.keys(ucsCoefficients ?? {})[0] ?? 0);

const cloneRouteForScenario = (sourceRoute: PCRoute): PCRoute => {
  const cloned = Object.create(Object.getPrototypeOf(sourceRoute));
  Object.assign(cloned, sourceRoute);
  cloned.routePoints = sourceRoute.routePoints.map((routePoint) => ({ ...routePoint }));
  return cloned;
};

export interface DerivedBinderConcentration {
  binderConcentrationByMass: number;
  clamped: boolean;
}

/**
 * Inverts the forward UCS path: solves for the binder concentration that hits `targetUCS`
 * at the supplied mass concentration, then rounds up to the binder interval and clamps to range.
 */
export const deriveBinderFromMassConcentration = (
  massConcentration: number,
  targetUCS: number,
  binderRange: BinderDto,
  ucsCoefficients?: UcsCoefficientsDto,
): DerivedBinderConcentration => {
  if (!ucsCoefficients || targetUCS <= 0 || massConcentration <= 0) {
    return { binderConcentrationByMass: 0, clamped: false };
  }
  const waterBinderRatio = calculateWCfromUCSandDays(targetUCS, ucsCoefficients.coefficient1, ucsCoefficients.coefficient2);
  const rawBinder = roundUpBinderConc(binderFromWaterBinderRatioAndBinder(waterBinderRatio, massConcentration), binderRange);
  const clampedBinder = Math.min(Math.max(rawBinder, binderRange.min), binderRange.max);
  return { binderConcentrationByMass: clampedBinder, clamped: clampedBinder !== rawBinder };
};

export const resolveUcsCoefficients = (rheologyData: RheologyDataDto, curingDays: number): UcsCoefficientsDto | undefined => {
  const hasUcsData = rheologyData.ucsCoefficients && Object.keys(rheologyData.ucsCoefficients).length > 0;
  if (!hasUcsData) return undefined;
  const days = curingDays === 0 ? getUCSTargetDays(rheologyData.ucsCoefficients) : curingDays;
  return rheologyData.ucsCoefficients[days];
};

export interface ScenarioRecomputeParams {
  model: Model;
  rheologyData: RheologyDataDto;
  route: PCRoute;
  massConcentration: number; // decimal
  targetUCS: number;
  curingDays: number;
  throughput: number;
  throughputControlType: ThroughputControlType;
}

/**
 * Recomputes a single optimiser scenario (slack flow, max pressure or target line) for a user supplied
 * mass concentration. The binder is re-derived so the target UCS is still met. The supplied route is
 * cloned before friction is recalculated, so the caller's route is never mutated.
 */
export const recomputeScenarioFromMassConcentration = ({
  model,
  rheologyData,
  route,
  massConcentration,
  targetUCS,
  curingDays,
  throughput,
  throughputControlType,
}: ScenarioRecomputeParams): PasteRecipe => {
  const ucsCoefficients = resolveUcsCoefficients(rheologyData, curingDays);
  const { binderConcentrationByMass } = deriveBinderFromMassConcentration(massConcentration, targetUCS, rheologyData.binder, ucsCoefficients);

  const baseRecipeState: RecipeState = {
    ...(model.recipeState ?? {}),
    totalDryConcentrationByMass: massConcentration,
    binderConcentrationByMass,
    tailingsSolidsDensity: rheologyData.tailingsDrySolidsDensity,
    binderParticleDensity: rheologyData.binderDryParticleDensity,
    carrierFluidDensity: rheologyData.carrierFluidDensity,
    throughputControlType,
    tailingsDryTonnage: throughputControlType === ThroughputControlType.DryTonnage ? throughput : undefined,
    wetTonnage: throughputControlType === ThroughputControlType.WetTonnage ? throughput : undefined,
    wetFlowRate: throughputControlType === ThroughputControlType.FlowRate ? throughput : undefined,
    rheologyDataSetId: rheologyData.rheologyDataSetId,
  };

  const scenarioRecipeState = updateRecipeState(
    model,
    baseRecipeState,
    massConcentration,
    binderConcentrationByMass,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    throughputControlType,
  );
  const updatedRoute = calculateFrictionForRoute(model, scenarioRecipeState, cloneRouteForScenario(route));

  return {
    cement: binderConcentrationByMass,
    concentrationByMass: massConcentration,
    actualUCSKpa: calculateActualUCS(massConcentration, binderConcentrationByMass, ucsCoefficients),
    actualPumpPressureKpa: updatedRoute?.pumpPressureKpa ?? 0,
    error: OptimisationErrorType.NONE,
    route: updatedRoute,
    slurryDensity: scenarioRecipeState.wetDensity ?? 1000,
  };
};

const checkPipeRatings = (
  nominalParams: NominalOptimisationParams | undefined,
  updatedRoute: PCRoute | undefined,
  maxPumpPressureKpa: number,
): boolean => {
  if (!updatedRoute?.routePoints) return true;
  const offsetPercent = nominalParams?.pipeRatingOffsetPercent ?? 0;
  const offsetPressureKpa = offsetPercent > 0 ? maxPumpPressureKpa * (offsetPercent / 100) : 0;
  return !updatedRoute.routePoints.some((rp) => {
    if (!rp.pipeDownstream) return false;
    const { pipeId } = rp.pipeDownstream;
    const isExcluded = offsetPercent > 0 && nominalParams?.excludedPipeIds?.includes(pipeId);
    const pressureAtPoint = rp.pressure ?? 0;
    return (pressureAtPoint + (isExcluded ? 0 : offsetPressureKpa)) > rp.pipeDownstream.pressureRating;
  });
};

const calculateYieldStress = (rheology: RheologyDataDto | undefined, concentrationByMass: number): number => {
  if (!rheology?.yieldStressCoefficients) return 0;
  const { coefficient1, coefficient2 } = rheology.yieldStressCoefficients;
  return calculateYieldfromConcentrationByMass(concentrationByMass * 100, coefficient1, coefficient2);
};

const pickHigherConcentration = (a?: PasteRecipe, b?: PasteRecipe): PasteRecipe | undefined => {
  if (!a) return b;
  if (!b) return a;
  return a.concentrationByMass >= b.concentrationByMass ? a : b;
};

const optimise = (
  model: Model,
  rheologyData: RheologyDataDto,
  waterBinderRatio: number,
  maxPumpPressureBar: number,
  initialRecipe: RecipeState,
  concentrationRange: MassConcentrationDto,
  binderRange: BinderDto,
  selectedRoute: PCRoute,
  ucsCoefficients?: UcsCoefficientsDto,
  nominalParams?: NominalOptimisationParams,
  throughputControlTypeOverride?: ThroughputControlType,
): PasteOptimiseResult => {
  const hasUcsData = !!ucsCoefficients;
  let maxPumpPressureKpa = model.project.pumpType === PumpType.Pump
    ? (maxPumpPressureBar === 0 ? 0.5 : maxPumpPressureBar) * 100
    : maxPumpPressureBar * 10 * 2; // Initial guess for gravity pump pressure

  let lastValidBinderConcentration: number | undefined;
  let sfSlackResult: PasteRecipe | undefined;
  let yieldSlackResult: PasteRecipe | undefined;
  let maxPressureResult: PasteRecipe | undefined;
  let optimisedResult: PasteRecipe | undefined;
  let prevValidSnapshot: PasteRecipe | undefined;

  for (let currentMassConcentration = concentrationRange.max;
    currentMassConcentration >= concentrationRange.min;
    currentMassConcentration -= concentrationRange.interval
  ) {
    let binderConcentrationByMass = hasUcsData
      ? roundUpBinderConc(
        binderFromWaterBinderRatioAndBinder(waterBinderRatio, currentMassConcentration),
        binderRange,
      )
      : 0;

    const binderAboveMax = hasUcsData && binderConcentrationByMass > binderRange.max;
    if (hasUcsData && binderConcentrationByMass < binderRange.min) {
      binderConcentrationByMass = binderRange.min;
    }
    const binderOutOfRange = binderAboveMax;
    if (binderOutOfRange) {
      lastValidBinderConcentration = binderConcentrationByMass;
    }

    let yieldBelowMin = false;
    let yieldAboveMax = false;
    if (nominalParams?.minYield !== undefined || nominalParams?.maxYield !== undefined) {
      const yieldStress = calculateYieldStress(rheologyData, currentMassConcentration);
      if (nominalParams?.minYield !== undefined && yieldStress < nominalParams.minYield) {
        yieldBelowMin = true;
      }
      if (nominalParams?.maxYield !== undefined && yieldStress > nominalParams.maxYield) {
        yieldAboveMax = true;
      }
    }

    // Skip friction calculation if binder out of range and yield above max (no useful result possible)
    if (binderOutOfRange && yieldAboveMax) {
      // eslint-disable-next-line no-continue
      continue;
    }

    const tempRecipe = updateRecipeState(
      model,
      initialRecipe,
      currentMassConcentration,
      binderConcentrationByMass,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      throughputControlTypeOverride,
    );

    const updatedRoute = calculateFrictionForRoute(model, tempRecipe, selectedRoute);
    model.setRecipeState(tempRecipe);
    if (model.project.pumpType === PumpType.Gravity) {
      maxPumpPressureKpa = (tempRecipe.wetDensity! / 1000) * 9.81 * maxPumpPressureBar;
    }
    if (isNumber(updatedRoute?.pumpPressureKpa)) {
      tempRecipe.pumpPressureKpa = updatedRoute!.pumpPressureKpa;
    }

    const boosterNodes = getPointsWithBoosterPressure(updatedRoute);
    const boosterPressureExceeded = boosterNodes.some((node) => isNumber(node.pressure)
      && isNumber(node.point.boosterPressure) && node.pressure! > node.point.boosterPressure * 100);
    const hasOverpressureWarning = updatedRoute?.warnings?.some((w) => w.code === 'OP');
    const hasSlackFlowWarning = updatedRoute?.warnings?.some((w) => w.code === 'SF');

    const currentSnapshot: PasteRecipe = {
      cement: binderConcentrationByMass,
      concentrationByMass: currentMassConcentration,
      actualUCSKpa: calculateActualUCS(currentMassConcentration, binderConcentrationByMass, ucsCoefficients),
      actualPumpPressureKpa: updatedRoute?.pumpPressureKpa || 0,
      error: OptimisationErrorType.NONE,
      route: structuredClone(updatedRoute),
      slurryDensity: tempRecipe.wetDensity!,
    };

    // Capture SF-based slack flow (first time SF warning appears, use prev step)
    if (sfSlackResult === undefined && hasSlackFlowWarning) {
      sfSlackResult = prevValidSnapshot ?? currentSnapshot;
    }

    // Capture yield-based slack flow (first time yield drops below min, use prev step)
    if (yieldSlackResult === undefined && yieldBelowMin) {
      yieldSlackResult = prevValidSnapshot ?? currentSnapshot;
    }

    // Track current step as snapshot for the next iteration (only when binder is valid)
    if (!binderOutOfRange) {
      prevValidSnapshot = currentSnapshot;
    }

    // Check for overpressure result
    if (!hasOverpressureWarning && maxPressureResult === undefined) {
      maxPressureResult = currentSnapshot;
    }

    const actualPumpPressureKpa = tempRecipe.pumpPressureKpa ?? updatedRoute?.pumpPressureKpa ?? 0;
    const passesPipeRatings = checkPipeRatings(nominalParams, updatedRoute, actualPumpPressureKpa);

    // Check for optimised result
    if (tempRecipe.pumpPressureKpa !== undefined
      && tempRecipe.pumpPressureKpa < maxPumpPressureKpa
      && !hasOverpressureWarning
      && !boosterPressureExceeded
      && passesPipeRatings
      && !yieldAboveMax
      && !binderOutOfRange
      && optimisedResult === undefined
    ) {
      optimisedResult = {
        cement: binderConcentrationByMass,
        concentrationByMass: currentMassConcentration,
        actualUCSKpa: calculateActualUCS(currentMassConcentration, binderConcentrationByMass, ucsCoefficients),
        actualPumpPressureKpa: tempRecipe?.pumpPressureKpa,
        error: OptimisationErrorType.NONE,
        route: structuredClone(updatedRoute),
        slurryDensity: tempRecipe.wetDensity!,
      };
    }

    // Use whichever slack trigger fires at the higher concentration (more restrictive)
    const slackFlowResult = pickHigherConcentration(sfSlackResult, yieldSlackResult);
    if (optimisedResult && slackFlowResult && maxPressureResult) {
      return {
        optimisedResult,
        slackFlowResult,
        maxPressureResult,
      };
    }
  }

  let errorType = OptimisationErrorType.OTHER;
  if (lastValidBinderConcentration !== undefined) {
    if (lastValidBinderConcentration < binderRange.min) {
      errorType = OptimisationErrorType.UCS_TOO_LOW;
    } else if (lastValidBinderConcentration > binderRange.max) {
      errorType = OptimisationErrorType.UCS_TOO_HIGH;
    }
  }

  return {
    optimisedResult: optimisedResult || {
      cement: 0,
      concentrationByMass: 0,
      actualUCSKpa: 0,
      actualPumpPressureKpa: 0,
      error: errorType,
      route: selectedRoute,
      slurryDensity: 1000,
    },
    slackFlowResult: pickHigherConcentration(sfSlackResult, yieldSlackResult),
    maxPressureResult,
  };
};

export const calculateRecipeFromStrength = (
  model: Model,
  rheologyData: RheologyDataDto,
  targetUCS: number,
  curingDays: number,
  maxPumpPressureBar: number,
  throughput: number,
  selectedRoute: PCRoute,
  throughputControlType: ThroughputControlType,
  nominalParams?: NominalOptimisationParams,
): PasteOptimiseResult => {
  const hasUcsData = rheologyData.ucsCoefficients && Object.keys(rheologyData.ucsCoefficients).length > 0;
  let days = 0;
  if (hasUcsData) {
    days = curingDays === 0 ? getUCSTargetDays(rheologyData.ucsCoefficients) : curingDays;
  }
  const ucsCoefficients = hasUcsData ? rheologyData.ucsCoefficients[days] : undefined;

  const tailingsDryTonnage = throughputControlType === ThroughputControlType.DryTonnage ? throughput : undefined;
  const wetTonnage = throughputControlType === ThroughputControlType.WetTonnage ? throughput : undefined;
  const wetFlowRate = throughputControlType === ThroughputControlType.FlowRate ? throughput : undefined;
  const initialRecipe: RecipeState = {
    totalDryConcentrationByMass: rheologyData.massConcentration.max,
    binderConcentrationByMass: hasUcsData ? rheologyData.binder.max : 0,
    tailingsSolidsDensity: rheologyData.tailingsDrySolidsDensity,
    binderParticleDensity: rheologyData.binderDryParticleDensity,
    carrierFluidDensity: rheologyData.carrierFluidDensity,
    throughputControlType,
    tailingsDryTonnage,
    wetTonnage,
    wetFlowRate,
    pumpPressureKpa: maxPumpPressureBar,
    rheologyDataSetId: rheologyData.rheologyDataSetId,
  };

  // UCS data exists but specific curing days not found - error
  if (hasUcsData && !ucsCoefficients) {
    return {
      optimisedResult: {
        cement: 0,
        concentrationByMass: 0,
        actualUCSKpa: 0,
        actualPumpPressureKpa: 0,
        error: OptimisationErrorType.OTHER,
        route: selectedRoute,
        slurryDensity: 1000,
      },
      slackFlowResult: undefined,
      maxPressureResult: undefined,
    };
  }

  const waterBinderRatio = ucsCoefficients
    ? calculateWCfromUCSandDays(targetUCS, ucsCoefficients.coefficient1, ucsCoefficients.coefficient2)
    : 0;

  return optimise(
    model,
    rheologyData,
    waterBinderRatio,
    maxPumpPressureBar,
    initialRecipe,
    rheologyData.massConcentration,
    rheologyData.binder,
    selectedRoute,
    ucsCoefficients,
    nominalParams,
    throughputControlType,
  );
};

// Gravity systems price the overpressure ceiling off the slurry column, so it moves with wet density.
const resolveMaxPumpPressureKpa = (model: Model, maxPumpPressureBar: number, wetDensity: number | undefined): number => {
  if (model.project.pumpType === PumpType.Pump) return (maxPumpPressureBar === 0 ? 0.5 : maxPumpPressureBar) * 100;
  if (model.project.pumpType === PumpType.Gravity) return ((wetDensity ?? 1000) / 1000) * 9.81 * maxPumpPressureBar;
  return maxPumpPressureBar * 10 * 2;
};

/**
 * The OP / SF / BP / OPP codes shown against a target line. Shared by the sweep target types, the
 * flow rate target, and the mass concentration edit handlers so an edited result is judged by the
 * same rules as an optimised one.
 */
export const assembleTargetLineWarnings = (
  model: Model,
  route: PCRoute | undefined,
  pumpPressureKpa: number | undefined,
  maxPumpPressureBar: number,
  wetDensity: number | undefined,
): string[] => {
  const maxPumpPressureKpa = resolveMaxPumpPressureKpa(model, maxPumpPressureBar, wetDensity);
  const boosterPressureExceeded = getPointsWithBoosterPressure(route).some((node) => isNumber(node.pressure)
    && isNumber(node.point.boosterPressure) && node.pressure! > node.point.boosterPressure * 100);

  return [
    route?.warnings?.some((w) => w.code === 'OP') ? 'OP' : undefined,
    route?.warnings?.some((w) => w.code === 'SF') ? 'SF' : undefined,
    boosterPressureExceeded ? 'BP' : undefined,
    isNumber(pumpPressureKpa) && pumpPressureKpa >= maxPumpPressureKpa ? 'OPP' : undefined,
  ].filter((code): code is string => code !== undefined);
};

export const describeTargetLineWarnings = (warnings: string[]) => ({
  hasErrors: warnings.length > 0,
  errorDescription: warnings.length > 0 ? warnings.join(', ') : undefined,
});

/** The optimised recipe's mix, which every flow rate target inherits verbatim. */
export interface OptimisedSpecificationForFlowRate {
  concentrationByMass: number;
  cement: number;
  actualUCSKpa: number;
}

/**
 * A flow rate target is the *same paste* at a different throughput: mass concentration and binder
 * are inherited from the optimised recipe, so only friction and pressures change. Nothing is solved
 * and the mass concentration sweep is skipped entirely — adjusting the optimised recipe's Cw
 * therefore moves every flow rate target with it.
 */
const calculateFlowRateTargetLineResult = (
  model: Model,
  rheologyData: RheologyDataDto,
  targetLine: OptimisationTargetLineInput,
  maxPumpPressureBar: number,
  selectedRoute: PCRoute,
  curingDays: number,
  targetUCS: number,
  optimisedSpecification: OptimisedSpecificationForFlowRate | undefined,
): OptimisationTargetLineResult => {
  const flowRateTarget = targetLine.flowRateTarget ?? 0;
  if (flowRateTarget <= 0) {
    return {
      ...targetLine, result: undefined, hasErrors: true, errorDescription: 'No flow rate target specified',
    };
  }
  if (!optimisedSpecification) {
    return {
      ...targetLine, result: undefined, hasErrors: true, errorDescription: 'Optimise the recipe before adding a flow rate target',
    };
  }

  // The mix is inherited wholesale from the optimised recipe — only throughput differs — so there is
  // nothing to solve. recomputeScenarioFromMassConcentration would re-derive binder from the UCS
  // target, which would drift from the optimised recipe, so build the state directly instead.
  const previousRecipeState = model.recipeState;
  try {
    const scenarioRecipeState = updateRecipeState(
      model,
      {
        totalDryConcentrationByMass: optimisedSpecification.concentrationByMass,
        binderConcentrationByMass: optimisedSpecification.cement,
        tailingsSolidsDensity: rheologyData.tailingsDrySolidsDensity,
        binderParticleDensity: rheologyData.binderDryParticleDensity,
        carrierFluidDensity: rheologyData.carrierFluidDensity,
        throughputControlType: ThroughputControlType.FlowRate,
        wetFlowRate: flowRateTarget,
        rheologyDataSetId: rheologyData.rheologyDataSetId,
      },
      optimisedSpecification.concentrationByMass,
      optimisedSpecification.cement,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      ThroughputControlType.FlowRate,
    );
    const updatedRoute = calculateFrictionForRoute(model, scenarioRecipeState, cloneRouteForScenario(selectedRoute));

    const result: PasteRecipe = {
      cement: optimisedSpecification.cement,
      concentrationByMass: optimisedSpecification.concentrationByMass,
      actualUCSKpa: optimisedSpecification.actualUCSKpa,
      actualPumpPressureKpa: updatedRoute?.pumpPressureKpa ?? 0,
      error: OptimisationErrorType.NONE,
      route: updatedRoute,
      slurryDensity: scenarioRecipeState.wetDensity ?? 1000,
    };

    const errors = assembleTargetLineWarnings(model, updatedRoute, result.actualPumpPressureKpa, maxPumpPressureBar, result.slurryDensity);

    return {
      ...targetLine, result, ...describeTargetLineWarnings(errors),
    };
  } finally {
    model.setRecipeState(previousRecipeState);
  }
};

export const calculateTargetLineResult = (
  model: Model,
  rheologyData: RheologyDataDto,
  targetLine: OptimisationTargetLineInput,
  maxPumpPressureBar: number,
  throughput: number,
  selectedRoute: PCRoute,
  throughputControlType: ThroughputControlType,
  curingDays: number,
  targetUCS: number,
  // The sweep target types solve for the Cw at which a metric hits its target and deliberately ignore
  // nominalParams. The FlowRate branch ignores them too — it inherits the optimised recipe's mix,
  // which was already solved under those constraints — and uses optimisedSpecification instead.
  nominalParams?: NominalOptimisationParams,
  optimisedSpecification?: OptimisedSpecificationForFlowRate,
): OptimisationTargetLineResult => {
  if (targetLine.targetType === OptimisationTargetType.FlowRate) {
    return calculateFlowRateTargetLineResult(model, rheologyData, targetLine, maxPumpPressureBar, selectedRoute, curingDays, targetUCS, optimisedSpecification);
  }

  const hasUcsData = rheologyData.ucsCoefficients && Object.keys(rheologyData.ucsCoefficients).length > 0;
  let days = 0;
  if (hasUcsData) {
    days = curingDays === 0 ? getUCSTargetDays(rheologyData.ucsCoefficients) : curingDays;
  }
  const ucsCoefficients = hasUcsData ? rheologyData.ucsCoefficients[days] : undefined;
  const waterBinderRatio = ucsCoefficients
    ? calculateWCfromUCSandDays(targetUCS, ucsCoefficients.coefficient1, ucsCoefficients.coefficient2)
    : 0;

  const tailingsDryTonnage = throughputControlType === ThroughputControlType.DryTonnage ? throughput : undefined;
  const wetTonnage = throughputControlType === ThroughputControlType.WetTonnage ? throughput : undefined;
  const wetFlowRate = throughputControlType === ThroughputControlType.FlowRate ? throughput : undefined;

  const initialRecipe: RecipeState = {
    totalDryConcentrationByMass: rheologyData.massConcentration.max,
    binderConcentrationByMass: hasUcsData ? rheologyData.binder.max : 0,
    tailingsSolidsDensity: rheologyData.tailingsDrySolidsDensity,
    binderParticleDensity: rheologyData.binderDryParticleDensity,
    carrierFluidDensity: rheologyData.carrierFluidDensity,
    throughputControlType,
    tailingsDryTonnage,
    wetTonnage,
    wetFlowRate,
    pumpPressureKpa: maxPumpPressureBar,
    rheologyDataSetId: rheologyData.rheologyDataSetId,
  };

  for (let currentMassConcentration = rheologyData.massConcentration.max;
    currentMassConcentration >= rheologyData.massConcentration.min;
    currentMassConcentration -= rheologyData.massConcentration.interval
  ) {
    let binderConcentrationByMass = hasUcsData
      ? roundUpBinderConc(
        binderFromWaterBinderRatioAndBinder(waterBinderRatio, currentMassConcentration),
        rheologyData.binder,
      )
      : 0;

    if (hasUcsData && binderConcentrationByMass > rheologyData.binder.max) {
      // eslint-disable-next-line no-continue
      continue;
    }
    if (hasUcsData && binderConcentrationByMass < rheologyData.binder.min) {
      binderConcentrationByMass = rheologyData.binder.min;
    }

    const tempRecipe = updateRecipeState(model, initialRecipe, currentMassConcentration, binderConcentrationByMass);
    const updatedRoute = calculateFrictionForRoute(model, tempRecipe, selectedRoute);

    if (isNumber(updatedRoute?.pumpPressureKpa)) {
      tempRecipe.pumpPressureKpa = updatedRoute!.pumpPressureKpa;
    }

    const actualUCSKpa = calculateActualUCS(currentMassConcentration, binderConcentrationByMass, ucsCoefficients);

    let matched = false;

    if (targetLine.targetType === OptimisationTargetType.Yield) {
      const yieldStress = calculateYieldStress(rheologyData, currentMassConcentration);
      if (yieldStress <= (targetLine.yieldTarget ?? 0)) {
        matched = true;
      }
    } else if (targetLine.targetType === OptimisationTargetType.PumpPressure) {
      const targetPressureKpa = (targetLine.pumpPressureTarget ?? 0) * 100;
      if (tempRecipe.pumpPressureKpa !== undefined && tempRecipe.pumpPressureKpa <= targetPressureKpa) {
        matched = true;
      }
    } else if (targetLine.targetType === OptimisationTargetType.KPaPerM) {
      if (updatedRoute?.routePoints && targetLine.targetPipeId) {
        const targetPipe = Object.values(model.pipes).find((p) => p.pipeId === targetLine.targetPipeId);
        if (targetPipe) {
          const velocity = targetPipe.getVelocity(tempRecipe.wetFlowRate ?? 0);
          const frictionLoss = calculateFriction(
            rheologyData.yieldStressCoefficients.coefficient1,
            rheologyData.yieldStressCoefficients.coefficient2,
            rheologyData.viscosityCoefficients.coefficient1,
            rheologyData.viscosityCoefficients.coefficient2,
            currentMassConcentration,
            targetPipe.insideDiameter,
            velocity,
            tempRecipe.wetDensity ?? 0,
            rheologyData.frictionLossFactorOfSafety,
          );
          if (frictionLoss <= (targetLine.kPaPerMTarget ?? 0)) {
            matched = true;
          }
        }
      }
    }

    if (matched) {
      const errors = assembleTargetLineWarnings(model, updatedRoute, tempRecipe.pumpPressureKpa, maxPumpPressureBar, tempRecipe.wetDensity);

      return {
        ...targetLine,
        result: {
          cement: binderConcentrationByMass,
          concentrationByMass: currentMassConcentration,
          actualUCSKpa,
          actualPumpPressureKpa: tempRecipe.pumpPressureKpa ?? 0,
          error: OptimisationErrorType.NONE,
          route: structuredClone(updatedRoute),
          slurryDensity: tempRecipe.wetDensity ?? 1000,
        },
        ...describeTargetLineWarnings(errors),
      };
    }
  }

  return {
    ...targetLine,
    result: undefined,
    hasErrors: true,
    errorDescription: 'No solution found',
  };
};

export const calculateStrengthAndPressureFromRecipe = (
  model: Model,
  maxPumpPressureBar: number,
  curingDays: number,
  dryConcentrationByMass: number,
  coefficient1: number,
  coefficient2: number,
  newTailingsSolidsDensity?: number,
  newBinderParticleDensity?: number,
  newCarrierFluidDensity?: number,
  newTailingsDryTonnage?: number,
  newWetFlowRate?: number,
  newWetTonnage?: number,

) => {
  const recipeState = model.recipeState as RecipeState;
  const selectedRoute = returnShortestRoute(model.routes) as PCRoute;
  const rheologyData = model.rheology.find((r) => r.rheologyDataSetId === recipeState?.rheologyDataSetId)
    ?? firstSelectableRheology(model.rheology) ?? model.rheology[0];
  const days = curingDays === 0 ? getUCSTargetDays(rheologyData.ucsCoefficients) : curingDays;
  const ucsCoefficients = rheologyData.ucsCoefficients[days];
  const binderConcentration = rheologyData.binder.max;
  const waterBinderRatio = waterBinderRatioFromConcentrationAndBinder(dryConcentrationByMass, binderConcentration);
  const calculatedUCS = calculateUCSfromWCandDays(waterBinderRatio, coefficient1, coefficient2);
  const unconfinedCompressiveStrength = parseInt(calculatedUCS.toPrecision(calculatedUCS > 9999 ? 5 : 4), 10);
  const updatedRecipe = updateRecipeState(
    model,
    recipeState,
    dryConcentrationByMass,
    binderConcentration,
    newTailingsSolidsDensity,
    newBinderParticleDensity,
    newCarrierFluidDensity,
    newTailingsDryTonnage,
    newWetFlowRate,
    newWetTonnage,
    recipeState?.pumpPressureKpa ?? 0,
    unconfinedCompressiveStrength,
  );
  model.setRecipeState(updatedRecipe);

  calculateFrictionForRoute(model);
  return optimise(
    model,
    rheologyData,
    waterBinderRatio,
    maxPumpPressureBar,
    updatedRecipe,
    rheologyData.massConcentration,
    rheologyData.binder,
    selectedRoute,
    ucsCoefficients,
  );
};
